import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, AlertTriangle, RefreshCw, Mail, Search, Download, Copy, CalendarClock, BellRing, Play } from 'lucide-react';
import { toast } from 'sonner';
import CarrierPacketBadge from '@/components/CarrierPacketBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CarrierCompliancePage = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, complete: 0, partial: 0, missing: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('all'); // all | pending | complete
  const [resending, setResending] = useState(null);
  const [coiRun, setCoiRun] = useState(null);
  const [coiRunning, setCoiRunning] = useState(false);

  const fetchCoiHistory = async () => {
    try {
      const r = await axios.get(`${API}/admin/coi-reminder/runs?limit=1`);
      setCoiRun(r.data.rows?.[0] || null);
    } catch { /* ignore */ }
  };

  const runCoiSweep = async () => {
    setCoiRunning(true);
    try {
      const r = await axios.post(`${API}/admin/coi-reminder/run`);
      setCoiRun(r.data);
      const { inspected = 0, sent = 0, suppressed = 0, failed = 0 } = r.data || {};
      toast.success(`COI sweep done — inspected ${inspected}, sent ${sent}, suppressed ${suppressed}, failed ${failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'COI sweep failed');
    } finally {
      setCoiRunning(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/carrier-compliance/list`);
      setRows(r.data.carriers || []);
      setTotals(r.data.totals || { total: 0, complete: 0, partial: 0, missing: 0, coi_expiring_soon: 0, coi_expired: 0 });
    } catch {
      toast.error('Failed to load carrier compliance list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); fetchCoiHistory(); }, []);

  const resend = async (email) => {
    setResending(email);
    try {
      const r = await axios.post(`${API}/carrier-compliance/resend`, { driver_email: email });
      if (r.data.sent) toast.success('Onboarding email sent');
      else toast.warning(`Email queued — link: ${r.data.upload_url}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setResending(null);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/carrier/onboarding/${token}`;
    navigator.clipboard?.writeText(url);
    toast.success('Link copied');
  };

  const filtered = rows.filter((r) => {
    if (tab === 'pending' && r.status === 'complete') return false;
    if (tab === 'complete' && r.status !== 'complete') return false;
    if (tab === 'coi-risk' && !r.coi_expiring_soon && !r.coi_expired) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [r.name, r.company_name, r.email, r.mc_number, r.dot_number]
      .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
  });

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="carrier-compliance-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Carrier Compliance</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Track W-9, COI, and ACH paperwork for every carrier you've worked with.
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-compliance-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatTile label="Carriers tracked" value={totals.total} color="text-foreground" testId="stat-total" />
          <StatTile label="Packet Complete" value={totals.complete} color="text-green-400" testId="stat-complete" />
          <StatTile label="Partial" value={totals.partial} color="text-amber-400" testId="stat-partial" />
          <StatTile label="Missing all" value={totals.missing} color="text-red-400" testId="stat-missing" />
          <StatTile label="COI at risk" value={(totals.coi_expiring_soon || 0) + (totals.coi_expired || 0)} color="text-orange-400" subtitle="<= 30 days / expired" testId="stat-coi-risk" />
        </div>

        <Card data-testid="coi-reminder-card">
          <CardContent className="pt-5 pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-md bg-orange-500/15 border border-orange-500/30">
                <BellRing className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <div className="font-medium text-sm">COI auto-reminder cron</div>
                <div className="text-xs text-muted-foreground mt-0.5" data-testid="coi-last-run">
                  {coiRun ? (
                    <>
                      Last run {new Date(coiRun.ran_at).toLocaleString()} — inspected{' '}
                      <span className="text-foreground">{coiRun.inspected ?? 0}</span>, sent{' '}
                      <span className="text-green-400">{coiRun.sent ?? 0}</span>, suppressed{' '}
                      <span className="text-zinc-400">{coiRun.suppressed ?? 0}</span>, failed{' '}
                      <span className="text-red-400">{coiRun.failed ?? 0}</span>
                    </>
                  ) : 'No runs recorded yet. Runs automatically every 6h.'}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={runCoiSweep}
              disabled={coiRunning}
              data-testid="run-coi-sweep-btn"
            >
              <Play className={`w-3.5 h-3.5 mr-1 ${coiRunning ? 'animate-pulse' : ''}`} />
              {coiRunning ? 'Running…' : 'Run sweep now'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-base">Carriers</CardTitle>
            <div className="flex items-center gap-2">
              {['all', 'pending', 'complete', 'coi-risk'].map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={tab === t ? 'default' : 'outline'}
                  onClick={() => setTab(t)}
                  data-testid={`tab-${t}`}
                >
                  {t === 'all' ? 'All' : t === 'pending' ? 'Pending' : t === 'complete' ? 'Complete' : 'COI at risk'}
                </Button>
              ))}
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="compliance-search"
                  className="h-9 pl-7 w-56 text-xs"
                  placeholder="Search name / email / MC"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-6 text-center">No carriers in this view.</p>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map((r) => (
                  <div key={r.email || r.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`carrier-row-${r.email}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.name || r.email}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {r.company_name ? `${r.company_name} · ` : ''}{r.email}
                        {r.mc_number ? ` · MC ${r.mc_number}` : ''}
                      </div>
                      {(r.coi_expired || r.coi_expiring_soon) && (
                        <div
                          className={`text-[11px] mt-1 inline-flex items-center gap-1 ${r.coi_expired ? 'text-red-400' : 'text-orange-400'}`}
                          data-testid={r.coi_expired ? `coi-expired-${r.email}` : `coi-expiring-${r.email}`}
                        >
                          <CalendarClock className="w-3 h-3" />
                          {r.coi_expired
                            ? `COI expired ${Math.abs(r.coi_days_to_expiry)} day(s) ago`
                            : `COI expires in ${r.coi_days_to_expiry} day(s) (${r.coi_expiry})`}
                        </div>
                      )}
                    </div>
                    <CarrierPacketBadge status={r.status} missing={r.missing} />
                    <div className="flex items-center gap-1">
                      {r.packet_token && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyLink(r.packet_token)}
                          data-testid={`copy-link-${r.email}`}
                          title="Copy onboarding link"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                      {r.status !== 'complete' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resend(r.email)}
                          disabled={resending === r.email}
                          data-testid={`resend-${r.email}`}
                        >
                          {resending === r.email
                            ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                            : <Mail className="w-3.5 h-3.5 mr-1" />}
                          Re-request
                        </Button>
                      )}
                      {r.status === 'complete' && r.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`${API}/carrier-compliance/file/${r.id}/w9`, '_blank')}
                          data-testid={`view-w9-${r.email}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> W-9
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const StatTile = ({ label, value, color, subtitle, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</div>}
    </CardContent>
  </Card>
);

export default CarrierCompliancePage;
